import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InboxSidebar } from "./InboxSidebar";
import { ChatArea } from "./ChatArea";
import styles from "../../css/messages.module.css";
import { ImageModal } from "./ImageModal";
import { useInboxContext } from "../../../../context/InboxContext";
import { BASE_URL } from "../../../../constants/apiConstants";

export const Messages = () => {
  const { t } = useTranslation();
  const inboxTypes = [
    {
      key: "admin-staff",
      label: t("admin_pages.messages.staff"),
      spacing: "start", // ps-3 in LTR, pe-3 in RTL
    },
    {
      key: "student",
      label: t("admin_pages.messages.parents"),
      // No spacing for middle item
    },
    {
      key: "admin-student",
      label: t("admin_pages.messages.private"),
      spacing: "end", // pe-3 in LTR, ps-3 in RTL
    },
  ];

  // Initialize state from URL hash
  const getInitialStateFromHash = () => {
    const hash = window.location.hash.substring(1); // Remove the #
    const params = new URLSearchParams(hash);
    
    return {
      activeInbox: params.get("inbox") || inboxTypes[0].key,
      activeGroupName: params.get("group") || null,
    };
  };

  const [stateFromHash] = useState(() => getInitialStateFromHash());
  const [activeInbox, setActiveInbox] = useState(stateFromHash.activeInbox);
  const [activeGroup, setActiveGroup] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const { inboxes, inboxesLoaded, updateInbox, inboxUnreadCounts } = useInboxContext();

  // Find the matching group in the loaded inboxes
  const findGroupInActiveInbox = useCallback((groupName) => {
    for (const group of inboxes[activeInbox]) {
      if (group.group_name === groupName) {
        return group;
      }
    }
    return null;
  }, [activeInbox, inboxes]);

  // Update URL hash when activeInbox or activeGroup changes
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (activeInbox) {
      params.set("inbox", activeInbox);
    }
    
    if (activeGroup?.group_name) {
      params.set("group", activeGroup.group_name);
    }
    
    // Update the hash
    const hashString = params.toString();
    window.location.hash = hashString ? `#${hashString}` : "";
  }, [activeInbox, activeGroup?.group_name]);

  // Restore full activeGroup object from group_name after inboxes are loaded
  useEffect(() => {
    if (stateFromHash.activeGroupName && inboxesLoaded[activeInbox] && inboxes[activeInbox]?.length > 0) {
      const foundGroup = findGroupInActiveInbox(stateFromHash.activeGroupName);
      if (foundGroup) {
        setActiveGroup(foundGroup);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxesLoaded[activeInbox]]);

  const getMemberName = (member) => {
    if (!member) return "Unknown Member";
    const name = `${member.first_name || ""} ${member.last_name || ""}`.trim();
    return name || member.username || "";
  };

  return (
    <section className={styles.messagesSection}>
      <p className={styles.pageTitle}>{t("admin_pages.messages.messages")}</p>
      <div className={styles.card}>
        {/* Inbox sidebar */}
        <InboxSidebar
          activeInbox={activeInbox}
          setActiveInbox={setActiveInbox}
          inboxTypes={inboxTypes}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          inboxes={inboxes}
          inboxUnreadCounts={inboxUnreadCounts}
          getMemberName={getMemberName}
        />

        {/* Chat Area */}
        <ChatArea
          activeInbox={activeInbox}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          updateInbox={updateInbox}
          getMemberName={getMemberName}
          setSelectedImage={setSelectedImage}
          setShowImageModal={setShowImageModal}
        />

        {/* Image Modal */}
        {showImageModal && selectedImage && (
          <ImageModal
            imageURL={`${BASE_URL}${selectedImage?.path}`}
            imageName={selectedImage?.name}
            setShowImageModal={setShowImageModal}
          />
        )}
      </div>
    </section>
  );
};
